import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VerifyCPF } from '../../custom-validators/cpf.validator';

export function cpfValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.replace(/\D/g, '');
    if (!value || value.length !== 11) return { cpfInvalid: true };
    if (/^(\d)\1{10}$/.test(value)) return { cpfInvalid: true };

    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(value[i]) * (10 - i);
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(value[9])) return { cpfInvalid: true };

    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(value[i]) * (11 - i);
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(value[10])) return { cpfInvalid: true };

    return null;
  };
}

@Component({
    selector: 'app-companion-certificate-modal',
    templateUrl: './companion-certificate-modal.component.html',
    styleUrls: ['./companion-certificate-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompanionCertificateModalComponent implements OnInit {
  modalForm!: FormGroup;
  isLoading = false;

  get companionsFormArray(): FormArray {
    return this.modalForm.get('companions') as FormArray;
  }

  constructor(
    public dialogRef: MatDialogRef<CompanionCertificateModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.modalForm = this.fb.group({
      companions: this.fb.array([])
    });

    const companions = this.data?.companions;
    if (companions?.length > 0) {
      companions.forEach((c: any) => this.companionsFormArray.push(this.createCompanionGroup(c)));
    } else {
      this.addCompanion();
    }
  }

  createCompanionGroup(data?: any): FormGroup {
    return this.fb.group({
      daysOff: [
        data?.daysOff ?? null,
        [Validators.required, Validators.min(1), Validators.max(365)]
      ],
      companionCpf: [
        data?.companionCpf ?? '',
        [Validators.required, VerifyCPF()]
      ],
      companionName: [
        data?.companionName ?? '',
        [Validators.required, Validators.minLength(3)]
      ]
    });
  }

  addCompanion(): void {
    this.companionsFormArray.push(this.createCompanionGroup());
  }

  removeCompanion(index: number): void {
    if (this.companionsFormArray.length > 1) {
      this.companionsFormArray.removeAt(index);
    } else {
      this.companionsFormArray.at(0).reset({ daysOff: null, companionCpf: '', companionName: '' });
    }
  }

  save(): void {
    this.companionsFormArray.controls.forEach(ctrl => ctrl.markAllAsTouched());

    if (this.modalForm.invalid) return;

    const validCompanions = this.companionsFormArray.value.filter(
      (c: any) => c.companionName?.trim()
    );
    this.dialogRef.close({ companions: validCompanions });
  }

  close(): void {
    this.dialogRef.close();
  }

  getControl(index: number, field: string): AbstractControl {
    return this.companionsFormArray.at(index).get(field)!;
  }
}